import { lazy, Suspense, useState } from "react";
import type { WorkplaceChatTokenPayload } from "@/lib/workplaceChatClient";
import type { MemberRow } from "./workplaceMessagesTypes";
import type { StreamChatLayout } from "./streamChatLayout";
import { WorkplaceOrgChannelAdmin } from "./WorkplaceOrgChannelAdmin";

const WorkplaceStreamChatEmbed = lazy(() => import("./WorkplaceStreamChatEmbed"));

export type WorkplaceVendorChatPanelProps = {
  tokenPayload: WorkplaceChatTokenPayload;
  displayName: string;
  members: MemberRow[];
  /** Full org roster for L3 channel membership (includes L1). */
  allOrgMembers?: MemberRow[];
  orgChannels?: string[];
  bridgeChannelSlugs: string[];
  mutedChannelIds: string[];
  onStartDm: (recipientUserIds: string[]) => Promise<{ ok: boolean; error?: string }>;
  onCreateOrgChannel?: (input: {
    slug: string;
    display_name?: string;
    member_user_ids?: string[];
  }) => Promise<{ ok: boolean; error?: string }>;
  onDeleteOrgChannel?: (slug: string) => Promise<{ ok: boolean; error?: string }>;
  onBridgeMute: (channelSlug: string, muted: boolean) => Promise<void>;
  onReload: () => void;
  layout?: StreamChatLayout;
  /** When true, channel mutes live in WorkplaceMessengerToolbar instead. */
  hideMuteSection?: boolean;
};

type StartPane = "dm" | "channels";

export function WorkplaceVendorChatPanel({
  tokenPayload,
  displayName,
  members,
  allOrgMembers,
  orgChannels = [],
  onStartDm,
  onCreateOrgChannel,
  onDeleteOrgChannel,
  onReload,
  layout = "contracted",
}: WorkplaceVendorChatPanelProps) {
  const [recipientId, setRecipientId] = useState("");
  const [dmBusy, setDmBusy] = useState(false);
  const [dmError, setDmError] = useState("");
  const [pane, setPane] = useState<StartPane | null>(null);
  const fill = layout !== "compact";
  const canManageChannels =
    tokenPayload.trust_level >= 3 && Boolean(onCreateOrgChannel) && Boolean(onDeleteOrgChannel);
  const channelRoster = allOrgMembers && allOrgMembers.length > 0 ? allOrgMembers : members;

  function togglePane(next: StartPane) {
    setPane((prev) => (prev === next ? null : next));
  }

  return (
    <div className={fill ? "flex h-full min-h-0 min-w-0 flex-col gap-2" : "space-y-2"}>
      <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-zinc-700">
        <div
          role="tablist"
          aria-label="Start conversation"
          className="relative grid grid-cols-2 bg-gray-100 p-0.5 dark:bg-zinc-800"
        >
          {pane ? (
            <span
              aria-hidden
              className={[
                "pointer-events-none absolute inset-y-0.5 w-[calc(50%-2px)] rounded-md bg-white shadow-sm transition-transform duration-200 dark:bg-zinc-900",
                pane === "channels" ? "translate-x-[calc(100%+2px)]" : "translate-x-0.5",
              ].join(" ")}
            />
          ) : null}
          <button
            type="button"
            role="tab"
            aria-selected={pane === "dm"}
            onClick={() => togglePane("dm")}
            className={[
              "relative z-[1] rounded-md px-2 py-1.5 text-[11px] font-semibold transition-colors",
              pane === "dm"
                ? "text-gray-900 dark:text-gray-50"
                : "text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200",
            ].join(" ")}
          >
            Start DM
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={pane === "channels"}
            onClick={() => togglePane("channels")}
            className={[
              "relative z-[1] rounded-md px-2 py-1.5 text-[11px] font-semibold transition-colors",
              pane === "channels"
                ? "text-gray-900 dark:text-gray-50"
                : "text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200",
            ].join(" ")}
          >
            Channels
          </button>
        </div>

        {pane === "dm" ? (
          <div className="p-2">
            <p className="text-[11px] font-medium text-gray-700 dark:text-gray-200">
              Start DM (policy-checked)
            </p>
            <div className="mt-2 flex gap-2">
              <select
                value={recipientId}
                onChange={(e) => setRecipientId(e.target.value)}
                className="min-w-0 flex-1 rounded-md border border-gray-200 px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-800"
              >
                <option value="">Select teammate</option>
                {members.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.name} (L{m.trust_level})
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={dmBusy || !recipientId}
                onClick={() => {
                  void (async () => {
                    setDmBusy(true);
                    setDmError("");
                    const r = await onStartDm([recipientId]);
                    setDmBusy(false);
                    if (!r.ok) {
                      setDmError(r.error || "Could not start DM");
                      return;
                    }
                    onReload();
                  })();
                }}
                className="rounded-md bg-blue-600 px-2 py-1 text-xs text-white disabled:opacity-50"
              >
                {dmBusy ? "…" : "Open"}
              </button>
            </div>
            {dmError ? (
              <p className="mt-1 text-[11px] text-red-600 dark:text-red-300">{dmError}</p>
            ) : null}
          </div>
        ) : pane === "channels" ? (
          <div className="p-2">
            {canManageChannels ? (
              <WorkplaceOrgChannelAdmin
                orgChannels={orgChannels}
                members={channelRoster}
                viewerUserId={tokenPayload.user_id}
                onCreate={onCreateOrgChannel!}
                onDelete={onDeleteOrgChannel!}
                onChanged={onReload}
              />
            ) : (
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                Channel create/membership is Level 3 only. Open channels you belong to in the list
                below.
              </p>
            )}
          </div>
        ) : null}
      </div>

      <div className={fill ? "min-h-0 min-w-0 flex-1" : undefined}>
        <Suspense fallback={<p className="text-xs text-gray-500">Loading chat…</p>}>
          <WorkplaceStreamChatEmbed
            tokenPayload={tokenPayload}
            displayName={displayName}
            layout={layout}
          />
        </Suspense>
      </div>
    </div>
  );
}
